/**
 * Messaging Accounts API Client
 * Manages the customer's linked messaging accounts (LINE 1:1 chat)
 * and their optional default branch for slip verification routing.
 */

import axios from '@/lib/api/axios-instance';

const BASE_URL = '/api/v2/messaging/accounts';

// ============================================================================
// Types
// ============================================================================

export interface MessagingAccount {
  id: string;
  platform: 'line' | 'facebook' | string;
  platform_user_id: string;
  platform_username: string | null;
  default_business_id: string | null;
  default_branch_id: string | null;
  is_active: boolean;
  linked_at: string;
  last_activity_at: string | null;
  business_name?: string | null;
  branch_name?: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

export const messagingAccountsAPI = {
  /**
   * List all messaging accounts linked to the current customer.
   */
  async getAll(): Promise<MessagingAccount[]> {
    const response = await axios.get<{ success: boolean; data: MessagingAccount[] }>(BASE_URL);
    return response.data.data ?? [];
  },

  /**
   * Set (or clear) the default branch for a 1:1 LINE chat account.
   * When set, all slips submitted via this account will route to the specified branch
   * instead of auto-resolving to the MAIN branch.
   *
   * @param accountId  — messaging_accounts.id
   * @param branchId   — UUID of the branch, or null to clear
   */
  async setDefaultBranch(
    accountId: string,
    branchId: string | null
  ): Promise<{ id: string; default_branch_id: string | null }> {
    const response = await axios.patch<{
      success: boolean;
      data: { id: string; default_branch_id: string | null };
    }>(`${BASE_URL}/${accountId}/default-branch`, { branch_id: branchId });
    return response.data.data;
  },
};
